'use client';

import { ERR_INTERNAL_500 } from '@korvo/core/lib/error-codes';
import { KorvoError } from '@korvo/core/lib/korvo-error';
import React, { createContext, type ReactNode, useCallback, useContext, useRef } from 'react';

import { type ChatSession } from '../types/session-manager';
import { ChatInstance } from './ChatInstance';
import { type ChatInstanceConfig } from './types';

type ChatInstanceManagerContextType = {
  createInstance: (config: ChatInstanceConfig) => string;
  destroyInstance: (instanceId: string) => void;
  getInstance: (instanceId: string) => ChatInstance | null;
  getAllInstances: () => Map<string, ChatInstance>;
  pauseInstance: (instanceId: string) => void;
  resumeInstance: (instanceId: string) => void;
  cleanupOldInstances: (maxAge: number) => void;
  loadSessionIntoInstance: (instanceId: string, session: ChatSession) => Promise<void>;
}

const ChatInstanceManagerContext = createContext<ChatInstanceManagerContextType | null>(null);

export function ChatInstanceManagerProvider({ children }: { children: ReactNode }) {
  const instancesRef = useRef<Map<string, ChatInstance>>(new Map());
  const pausedInstancesRef = useRef<Set<string>>(new Set());

  const createInstance = useCallback((config: ChatInstanceConfig): string => {
    // Check if instance already exists for this session
    const existingInstances = Array.from(instancesRef.current.values());
    const existingInstance = existingInstances.find(inst => inst.getSessionId() === config.sessionId);
    
    if (existingInstance) {
      return existingInstance.getInstanceId();
    }
    
    const instanceId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const instance = new ChatInstance(instanceId, config);
    instancesRef.current.set(instanceId, instance);
    return instanceId;
  }, []);

  const destroyInstance = useCallback((instanceId: string) => {
    const instance = instancesRef.current.get(instanceId);
    if (instance) {
      instance.destroy();
      instancesRef.current.delete(instanceId);
      pausedInstancesRef.current.delete(instanceId);
    }
  }, []);

  const getInstance = useCallback((instanceId: string): ChatInstance | null => {
    return instancesRef.current.get(instanceId) || null;
  }, []);

  const getAllInstances = useCallback((): Map<string, ChatInstance> => {
    return new Map(instancesRef.current);
  }, []);

  const pauseInstance = useCallback((instanceId: string) => {
    const instance = instancesRef.current.get(instanceId);
    if (instance) {
      instance.pause();
      pausedInstancesRef.current.add(instanceId);
    }
  }, []);

  const resumeInstance = useCallback((instanceId: string) => {
    const instance = instancesRef.current.get(instanceId);
    if (instance) {
      instance.resume();
      pausedInstancesRef.current.delete(instanceId);
    }
  }, []);

  const cleanupOldInstances = useCallback((maxAge: number = 30 * 60 * 1000) => { // 30 minutes default
    const now = Date.now();
    const instancesToDestroy: string[] = [];
    
    instancesRef.current.forEach((instance, instanceId) => {
      const session = instance.getState().session;
      if (session && (now - session.updatedAt) > maxAge) {
        instancesToDestroy.push(instanceId);
      }
    });
    
    instancesToDestroy.forEach(instanceId => {
      destroyInstance(instanceId);
    });
  }, [destroyInstance]);

  const loadSessionIntoInstance = useCallback(async (instanceId: string, session: ChatSession) => {
    const instance = instancesRef.current.get(instanceId);
    if (instance) {
      await instance.loadSession(session);
    }
  }, []);

  const value: ChatInstanceManagerContextType = {
    createInstance,
    destroyInstance,
    getInstance,
    getAllInstances,
    pauseInstance,
    resumeInstance,
    cleanupOldInstances,
    loadSessionIntoInstance
  };

  return React.createElement(
    ChatInstanceManagerContext.Provider,
    { value },
    children
  );
}

export function useChatInstanceManager(): ChatInstanceManagerContextType {
  const context = useContext(ChatInstanceManagerContext);
  if (!context) {
    throw new KorvoError(ERR_INTERNAL_500, 1, {
      message: 'useChatInstanceManager must be used within a ChatInstanceManagerProvider',
    });
  }
  return context;
}
